import { Case, CaseStatus } from '@/@types/generated'
import { UserStampTooltip } from '@/components/Atoms/UserStampTooltip'
import format from '@/lib/format'
import { buildPath } from '@/routes'
import { ExternalLinkIcon, Trash2Icon } from 'lucide-react'

interface CaseDeletedSummaryProps {
  case: Case
}

// Full, read-only record of how/why a case was deleted - shown at the top of the case card.
// Legacy deletions (made before delete reasons existed) have no reason or original-case link
// and must not look broken.
export function CaseDeletedSummary({ case: _case }: CaseDeletedSummaryProps) {
  if (!_case.deleted) return null

  const appliedAmount = _case.penalty?.appliedAmount ?? 0
  const outstandingAmount = _case.penalty?.outstandingAmount ?? 0
  const isLegacy = !_case.deleteReason

  return (
    <div
      role="note"
      className="bg-destructive/10 border-destructive flex gap-3 rounded-lg border p-4"
    >
      <div className="bg-destructive-background flex size-8 shrink-0 items-center justify-center rounded-full">
        <Trash2Icon className="text-destructive-foreground size-4" />
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <div>
          <p className="font-semibold">This case was deleted</p>
          <UserStampTooltip
            by={_case.deleted}
            className="text-muted-foreground text-sm"
          />
        </div>

        <dl className="grid grid-cols-[112px_1fr] gap-x-2 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Reason</dt>
          <dd className={isLegacy ? 'text-muted-foreground' : 'font-semibold'}>
            {isLegacy
              ? 'Not recorded (deleted before reasons were required)'
              : _case.deleteReason!.name}
          </dd>

          {_case.originalCase && (
            <>
              <dt className="text-muted-foreground">Original case</dt>
              <dd>
                <a
                  href={buildPath('/cases/:caseId', {
                    caseId: String(_case.originalCase.id),
                  })}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary inline-flex items-center gap-1 font-mono hover:underline"
                >
                  {_case.originalCase.code}
                  <ExternalLinkIcon className="size-3" />
                </a>
                {' · '}
                {_case.originalCase.companyName}
                {' · '}
                {format.Enum(CaseStatus, _case.originalCase.status)}
              </dd>
            </>
          )}

          {_case.deleteNotes && (
            <>
              <dt className="text-muted-foreground">Notes</dt>
              <dd className="break-words">{_case.deleteNotes}</dd>
            </>
          )}

          {appliedAmount > 0 && (
            <>
              <dt className="text-muted-foreground">Payments</dt>
              <dd>
                {format.Currency(appliedAmount)} applied ·{' '}
                <a
                  href={`${buildPath('/payments')}?businessEntityId=${_case.businessEntityId ?? ''}`}
                  className="text-primary hover:underline"
                >
                  Open in Payments
                </a>
              </dd>
            </>
          )}

          {outstandingAmount > 0 && (
            <>
              <dt className="text-muted-foreground">Penalty</dt>
              <dd>
                {format.Currency(outstandingAmount)} unpaid · deleted with case
              </dd>
            </>
          )}
        </dl>
      </div>
    </div>
  )
}
